import React from 'react';
import { Link } from 'react-router-dom';
import Icon from 'components/AppIcon';
import GoBackButton from '../../components/GoBackButton';
import FooterSection from '../landing-page/components/FooterSection';
import { useLanguage } from '../../contexts/LanguageContext';

const AboutUs = () => {
  const { t, language } = useLanguage();

  const values = [
    {
      icon: 'Brain',
      title: language === 'bn' ? 'এআই-চালিত বুদ্ধিমত্তা' : 'AI-Powered Intelligence',
      description: language === 'bn' ? 'আমরা অত্যাধুনিক কৃত্রিম বুদ্ধিমত্তা ব্যবহার করে সম্পত্তির অন্তর্দৃষ্টি প্রদান করি।' : 'We harness cutting-edge artificial intelligence to deliver property insights that were previously only available to industry insiders.',
    },
    {
      icon: 'Shield',
      title: language === 'bn' ? 'স্বচ্ছতা প্রথমে' : 'Transparency First',
      description: language === 'bn' ? 'প্রতিটি বিশ্লেষণে আস্থার স্কোর এবং ডেটা উৎস থাকে।' : 'Every analysis comes with confidence scores and data sources so you always know the basis of our recommendations.',
    },
    {
      icon: 'Globe',
      title: language === 'bn' ? 'দ্বৈত-বাজার দক্ষতা' : 'Dual-Market Expertise',
      description: language === 'bn' ? 'অস্ট্রেলিয়া এবং UAE সম্পত্তি বাজারে গভীর বিশেষজ্ঞতা।' : 'Deep specialisation in both Australian and UAE property markets, giving you a truly global perspective on real estate investment.',
    },
    {
      icon: 'Users',
      title: language === 'bn' ? 'সম্প্রদায়-চালিত' : 'Community Driven',
      description: language === 'bn' ? 'আমরা ক্রেতা, বিক্রেতা, এজেন্ট এবং পেশাদারদের একটি বিশ্বস্ত ইকোসিস্টেমে সংযুক্ত করি।' : 'We connect buyers, sellers, agents, and professionals in a trusted ecosystem built on verified credentials and real reviews.',
    },
  ];

  const milestones = [
    { year: '2023', event: language === 'bn' ? 'সম্পত্তি বিশ্লেষণকে গণতান্ত্রিক করার দৃষ্টিভঙ্গি নিয়ে Opel Khan কর্তৃক ListingLens প্রতিষ্ঠিত' : 'ListingLens founded by Opel Khan with a vision to democratise property intelligence' },
    { year: '2024', event: language === 'bn' ? 'সমস্ত ৮টি অস্ট্রেলিয়ান রাজ্য ও অঞ্চল কভার করে এআই-চালিত সম্পত্তি বিশ্লেষণ চালু' : 'Launched AI-powered property analysis covering all 8 Australian states and territories' },
    { year: '2025', event: language === 'bn' ? 'RERA কমপ্লায়েন্স টুলস এবং দুবাই/আবুধাবি ডিপ-ডাইভ অ্যানালিটিক্স সহ UAE বাজারে সম্প্রসারিত' : 'Expanded to UAE market with RERA compliance tools and Dubai/Abu Dhabi deep-dive analytics' },
    { year: '2026', event: language === 'bn' ? '৫০০+ যাচাইকৃত সম্পত্তি পেশাদারদের সংযুক্ত করে পেশাদার মার্কেটপ্লেস চালু' : 'Professional marketplace launched connecting 500+ verified property professionals' },
  ];

  const stats = [
    { value: '50,000+', label: language === 'bn' ? t('propertiesAnalysed') : 'Properties Analysed' },
    { value: '8', label: language === 'bn' ? t('australianStatesCovered') : 'Australian States Covered' },
    { value: '7', label: language === 'bn' ? t('uaeEmiratesCovered') : 'UAE Emirates Covered' },
    { value: '500+', label: language === 'bn' ? t('verifiedProfessionalsCount') : 'Verified Professionals' },
  ];

  return (
    <div className="min-h-screen" style={{ background: '#050A14' }}>
      {/* Nav */}
      <nav className="border-b border-border/30 px-4 md:px-8 h-16 flex items-center justify-between" style={{ background: 'rgba(5,10,20,0.95)', backdropFilter: 'blur(12px)' }}>
        <Link to="/" className="flex items-center gap-3">
          <img
            src="/assets/images/logo_LL-1772022913346.jpeg"
            alt="ListingLens logo"
            className="h-9 w-auto rounded-lg object-cover"
            style={{ boxShadow: '0 0 12px rgba(0,212,255,0.4)', border: '1px solid rgba(0,212,255,0.3)', maxWidth: '130px' }}
          />
        </Link>
        <div className="flex items-center gap-4">
          <GoBackButton fallbackPath="/" label={t('goBack')} />
          <Link to="/" className="text-sm text-muted-foreground hover:text-primary transition-colors">{t('home')}</Link>
          <Link to="/contact-us" className="text-sm px-4 py-2 rounded-lg font-medium transition-colors" style={{ background: 'rgba(0,212,255,0.1)', color: '#00D4FF', border: '1px solid rgba(0,212,255,0.3)' }}>{t('contactUs')}</Link>
        </div>
      </nav>
      {/* Hero */}
      <section className="relative py-20 md:py-28 px-4 md:px-8 text-center overflow-hidden">
        <div className="absolute inset-0 pointer-events-none" style={{
          backgroundImage: `radial-gradient(circle at 50% 0%, rgba(0,212,255,0.08) 0%, transparent 60%)`
        }} />
        <div className="relative max-w-4xl mx-auto">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium mb-6" style={{ background: 'rgba(0,212,255,0.1)', color: '#00D4FF', border: '1px solid rgba(0,212,255,0.2)' }}>
            <Icon name="Info" size={14} />
            {t('ourStory')}
          </div>
          <h1 className="text-4xl md:text-6xl font-black mb-6" style={{ color: '#F0F4FF' }}>
            {language === 'bn' ? 'সম্পর্কে ' : 'About '}<span style={{ color: '#00D4FF' }}>ListingLens</span>
          </h1>
          <p className="text-lg md:text-xl leading-relaxed max-w-3xl mx-auto" style={{ color: 'rgba(240,244,255,0.7)' }}>
            {language === 'bn' ? 'Opel Khan প্রতিষ্ঠিত ListingLens হলো অস্ট্রেলিয়া এবং UAE-এর সবচেয়ে উন্নত এআই-চালিত সম্পত্তি বিশ্লেষণ প্ল্যাটফর্ম।' : "Founded by Opel Khan, ListingLens is Australia and UAE's most advanced AI-powered property intelligence platform — built to give every buyer, seller, and investor the same data advantages previously reserved for industry insiders."}
          </p>
        </div>
      </section>
      {/* Stats */}
      <section className="py-12 px-4 md:px-8">
        <div className="max-w-6xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-6">
          {stats?.map((stat) => (
            <div key={stat?.label} className="text-center p-6 rounded-2xl" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(0,212,255,0.15)' }}>
              <div className="text-3xl md:text-4xl font-black mb-2" style={{ color: '#00D4FF' }}>{stat?.value}</div>
              <div className="text-sm" style={{ color: 'rgba(240,244,255,0.6)' }}>{stat?.label}</div>
            </div>
          ))}
        </div>
      </section>
      {/* Founder Section */}
      <section className="py-16 md:py-24 px-4 md:px-8">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium mb-6" style={{ background: 'rgba(123,47,255,0.1)', color: '#7B2FFF', border: '1px solid rgba(123,47,255,0.2)' }}>
                <Icon name="User" size={14} />
                {t('founderCEO')}
              </div>
              <h2 className="text-3xl md:text-4xl font-bold mb-4" style={{ color: '#F0F4FF' }}>Opel Khan</h2>
              <p className="text-base leading-relaxed mb-4" style={{ color: 'rgba(240,244,255,0.7)' }}>
                {language === 'bn' ? 'Opel Khan একটি স্পষ্ট লক্ষ্য নিয়ে ListingLens প্রতিষ্ঠা করেছেন: পেশাদার-গ্রেড বিশ্লেষণ সবার কাছে সহজলভ্য করে সম্পত্তি বাজারে সমান সুযোগ তৈরি করা।' : 'Opel Khan founded ListingLens with a clear mission: to level the playing field in property markets by making professional-grade analysis accessible to everyone.'}
              </p>
              <p className="text-base leading-relaxed mb-4" style={{ color: 'rgba(240,244,255,0.7)' }}>
                {language === 'bn' ? 'অস্ট্রেলিয়া এবং UAE রিয়েল এস্টেট বাজারে গভীর দক্ষতার সাথে, Opel বুঝতে পেরেছিলেন যে ক্রেতা এবং বিনিয়োগকারীরা শিল্প পেশাদারদের মতো একই ডেটা এবং অন্তর্দৃষ্টি ছাড়াই জীবন-পরিবর্তনকারী আর্থিক সিদ্ধান্ত নিচ্ছেন।' : 'With deep expertise in both Australian and UAE real estate markets, Opel recognised that buyers and investors were making life-changing financial decisions without access to the same data and insights that industry professionals relied on.'}
              </p>
              <p className="text-base leading-relaxed mb-6" style={{ color: 'rgba(240,244,255,0.7)' }}>
                {language === 'bn' ? 'ListingLens এই দৃষ্টিভঙ্গি থেকে জন্ম নিয়েছে — একটি প্ল্যাটফর্ম যা প্রতিটি সম্পত্তির সিদ্ধান্তকে ক্ষমতায়িত করতে এআই, রিয়েল-টাইম বাজার ডেটা এবং একটি বিশ্বস্ত পেশাদার নেটওয়ার্ককে একত্রিত করে।' : 'ListingLens was born from this vision — a platform that combines AI, real-time market data, and a trusted professional network to empower every property decision.'}
              </p>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: 'rgba(0,212,255,0.1)', border: '1px solid rgba(0,212,255,0.2)' }}>
                  <Icon name="Mail" size={18} color="#00D4FF" />
                </div>
                <span style={{ color: 'rgba(240,244,255,0.7)' }}>opel@listinglens.live</span>
              </div>
            </div>
            <div className="relative">
              <div className="rounded-3xl p-8" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(0,212,255,0.15)' }}>
                <div className="w-24 h-24 rounded-2xl flex items-center justify-center mx-auto mb-6" style={{ background: 'linear-gradient(135deg, rgba(0,212,255,0.2), rgba(123,47,255,0.2))', border: '1px solid rgba(0,212,255,0.3)' }}>
                  <Icon name="User" size={40} color="#00D4FF" />
                </div>
                <h3 className="text-xl font-bold text-center mb-2" style={{ color: '#F0F4FF' }}>Opel Khan</h3>
                <p className="text-center text-sm mb-6" style={{ color: '#00D4FF' }}>{t('founderCEO')}, ListingLens</p>
                <div className="space-y-3">
                  {[
                    language === 'bn' ? 'অস্ট্রেলিয়ান সম্পত্তি বাজার' : 'Australian Property Markets',
                    language === 'bn' ? 'UAE রিয়েল এস্টেট ও RERA' : 'UAE Real Estate & RERA',
                    language === 'bn' ? 'এআই ও প্রপটেক উদ্ভাবন' : 'AI & PropTech Innovation',
                    language === 'bn' ? 'বিনিয়োগ কৌশল' : 'Investment Strategy'
                  ]?.map((skill) => (
                    <div key={skill} className="flex items-center gap-3">
                      <div className="w-2 h-2 rounded-full" style={{ background: '#00D4FF' }} />
                      <span className="text-sm" style={{ color: 'rgba(240,244,255,0.7)' }}>{skill}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* Mission & Vision */}
      <section className="py-16 md:py-24 px-4 md:px-8" style={{ background: 'rgba(255,255,255,0.02)' }}>
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4" style={{ color: '#F0F4FF' }}>{t('missionVision')}</h2>
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="p-8 rounded-2xl" style={{ background: 'rgba(0,212,255,0.05)', border: '1px solid rgba(0,212,255,0.2)' }}>
              <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4" style={{ background: 'rgba(0,212,255,0.15)' }}>
                <Icon name="Target" size={24} color="#00D4FF" />
              </div>
              <h3 className="text-xl font-bold mb-3" style={{ color: '#F0F4FF' }}>{t('ourMission')}</h3>
              <p className="leading-relaxed" style={{ color: 'rgba(240,244,255,0.7)' }}>
                {t('ourMissionText')}
              </p>
            </div>
            <div className="p-8 rounded-2xl" style={{ background: 'rgba(123,47,255,0.05)', border: '1px solid rgba(123,47,255,0.2)' }}>
              <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4" style={{ background: 'rgba(123,47,255,0.15)' }}>
                <Icon name="Eye" size={24} color="#7B2FFF" />
              </div>
              <h3 className="text-xl font-bold mb-3" style={{ color: '#F0F4FF' }}>{language === 'bn' ? 'আমাদের দৃষ্টিভঙ্গি' : 'Our Vision'}</h3>
              <p className="leading-relaxed" style={{ color: 'rgba(240,244,255,0.7)' }}>
                {t('ourVisionText')}
              </p>
            </div>
          </div>
        </div>
      </section>
      {/* Values */}
      <section className="py-16 md:py-24 px-4 md:px-8">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4" style={{ color: '#F0F4FF' }}>{t('whatMakesUsUnique')}</h2>
            <p className="text-lg max-w-2xl mx-auto" style={{ color: 'rgba(240,244,255,0.6)' }}>{language === 'bn' ? 'আমাদের মূল মূল্যবোধ আমরা যা তৈরি করি এবং প্রতিটি সিদ্ধান্তকে পরিচালিত করে।' : 'Our core values drive everything we build and every decision we make.'}</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {values?.map((value) => (
              <div key={value?.title} className="p-6 rounded-2xl transition-all hover:scale-105" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(0,212,255,0.1)' }}>
                <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4" style={{ background: 'rgba(0,212,255,0.1)' }}>
                  <Icon name={value?.icon} size={22} color="#00D4FF" />
                </div>
                <h3 className="font-bold mb-2" style={{ color: '#F0F4FF' }}>{value?.title}</h3>
                <p className="text-sm leading-relaxed" style={{ color: 'rgba(240,244,255,0.6)' }}>{value?.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
      {/* Timeline */}
      <section className="py-16 md:py-24 px-4 md:px-8" style={{ background: 'rgba(255,255,255,0.02)' }}>
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4" style={{ color: '#F0F4FF' }}>{t('ourJourney')}</h2>
          </div>
          <div className="space-y-6">
            {milestones?.map((milestone) => (
              <div key={milestone?.year} className="flex gap-6 items-start">
                <div className="flex-shrink-0 w-16 h-16 rounded-xl flex items-center justify-center font-bold text-sm" style={{ background: 'rgba(0,212,255,0.1)', color: '#00D4FF', border: '1px solid rgba(0,212,255,0.2)' }}>
                  {milestone?.year}
                </div>
                <div className="flex-1 p-5 rounded-xl" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
                  <p style={{ color: 'rgba(240,244,255,0.8)' }}>{milestone?.event}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
      {/* CTA */}
      <section className="py-16 md:py-24 px-4 md:px-8 text-center">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold mb-4" style={{ color: '#F0F4FF' }}>{t('readyToMakeSmarter')}</h2>
          <p className="text-lg mb-8" style={{ color: 'rgba(240,244,255,0.6)' }}>{t('joinThousands')}</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/authentication" className="px-8 py-4 rounded-xl font-bold text-base transition-all hover:scale-105" style={{ background: 'linear-gradient(135deg, #00D4FF, #7B2FFF)', color: '#fff' }}>
              {t('getStartedFree')}
            </Link>
            <Link to="/contact-us" className="px-8 py-4 rounded-xl font-bold text-base transition-all" style={{ background: 'rgba(255,255,255,0.05)', color: '#F0F4FF', border: '1px solid rgba(255,255,255,0.1)' }}>
              {t('contactUs')}
            </Link>
          </div>
        </div>
      </section>
      <FooterSection />
    </div>
  );
};

export default AboutUs;
